import React from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Form from "react-bootstrap/Form";

// app-core
import { useAppDispatch, useAppSelector } from "./core/redux/hooks";
import {
    clearAuthSlice,
    fetchingUserDetails,
    isAuthenticated,
    setAgentStatus,
    setAllAgentStatusDetails,
    setAllKioskStatusDetails,
    setIsAuthenticated,
    setUserDetails,
} from "./core/redux/authSlice";
import { AppRoutes } from "./core/routes/routes";
import { isLogin } from "./core/services/authservice";
import { AuthInterceptor } from "./core/interceptor/auth-interceptor";
import { GetApiMethod } from "./core/services/apiService";
import {
    DisplayToaster,
    GetTenantId,
    GetWebSocketConnection,
    disconnectWebSocketConnection,
    getMobileOperatingSystem,
    numberOnly,
    removeBrbTimeStamp,
    removeBrbTimeout,
    infoUserForSocketIssue,
    loggerHelper,
    checkCallSid
} from "./core/services/sharedService";
import {
    DtoAgentNotification,
    DtoAgentStatus,
    DtoIncomingNotification,
} from "./core/dto/dashboard.dto";
import {
    appendVoiceCallInstance,
    initializeAudioDevice,
    playPauseRinger,
    removeInComingVoiceCallNotification,
    resetDisconnectCall,
    setAgentNotifications,
    setNotificationsListener,
    setVoiceCallInstance,
    terminateVoiceCall,
    setAudioCallTimer,
    resetAgentNotifications,
    endOnHoldVoiceCall,
    updateDndTimer,
    enableAddParticipantButton,
    updateVideoRoomDetailsAddParticipantButton,
    endOnHoldVideoCall,
    triggerVideoCallEndFunction,
    setErrorMessageToaster,
    setLocationsData,
    setLocationDetails,
    setAudioOutputDeviceChanged,
    setEnableDisableDropDown,
    setVoiceMailNotificationTrigger
} from "./core/redux/notificationsSlice";

// Component
import AppLoader from "./components/AppLoader/AppLoader";
import NavigationBar from "./components/NavigationBar/NavigationBar";
import ToastView from "./components/AppToast/Toast";
import { showToaster } from "./core/redux/toastSlice";
import AppModal from "./components/AppModal/AppModal";

// assets
import "bootstrap-icons/font/bootstrap-icons.css";
import ringer from "./assets/notification_ringer.mp3";

function App() {
    // calling axios interceptor
    AuthInterceptor();

    let settingsState = useAppSelector((state) => state.settings);
    // redux
    let authState = useAppSelector((state) => state.auth);
    let notificationsSliceSelector: DtoAgentNotification = useAppSelector(
        (state) => state.agentNotification
    );
    const authenticated = useAppSelector(isAuthenticated);
    const dispatch = useAppDispatch();
    const currentUserTenantId = GetTenantId();

    // state variable
    const [appState, setAppState] = React.useState<{
        [index: string]: boolean;
    }>({
        showNotificationAlertModal: false,
        showBeRightBackAlertModal: false,
        showBeRightBackConfirmForm: false,
    });
    const [timeOutInMin, setTimeOutInMin] = React.useState<any>('2');
    const [inOtherPage, setInOtherPage] = React.useState<boolean>(false);

    // useRef
    let fetchingUserData = React.useRef<boolean>(false);
    const notificationRingerRef = React.useRef<any>();
    let device = React.useRef();
    let deviceListenerStarted = React.useRef<boolean>(false);
    let currentUserEmail = React.useRef<string>();
    let currentUserStatusRef = React.useRef<string>();
    const intervalRef = React.useRef<number | null>(null);
    const timeOutTimeMinRef = React.useRef<number>(timeOutInMin ? +timeOutInMin : 0);

    // socket
    const socket = GetWebSocketConnection()!;
    const Device = require("@twilio/voice-sdk").Device;

    //router
    const location = useLocation();
    const navigate = useNavigate();

    const logger = React.useCallback((message: string, details: any) => {
        loggerHelper(message, details);
    }, []);

    const fetchUserDetails = React.useCallback(() => {
        dispatch(fetchingUserDetails(true));
        const url =
            process.env.REACT_APP_ADMIN === localStorage.getItem("event")
                ? "admin/user/profile"
                : "tenant/agent/profile";
        GetApiMethod(url).then((res) => {
            dispatch(fetchingUserDetails(false));
            dispatch(setIsAuthenticated(true));
            if (res && res.data && res.data.user) {
                dispatch(setUserDetails(res.data.user));
            }
        });
    }, []);

    const redirectToDashboardPage = React.useCallback(() => {
        navigate("/dashboard");
        setAppState({
            ...appState,
            showNotificationAlertModal: false,
        });
        setInOtherPage(false);
    }, []);

    // For tracking the notification.
    const notificationListener = React.useCallback((message: DtoIncomingNotification, callback: any) => {
        const notificationMessage = { ...message };
        notificationMessage.callback = callback;
        message = { ...notificationMessage };
        dispatch(setNotificationsListener(message));
    }, []);

    const voiceMailNotificationListener = React.useCallback((message: any) => {
        let currentUnreadVoiceMailCount = localStorage.getItem("voiceMailNotification");
        let countOfVMS = currentUnreadVoiceMailCount ? +currentUnreadVoiceMailCount : null;
        localStorage.setItem('voiceMailNotification', countOfVMS ? (`${countOfVMS + 1}`) : "1");
        dispatch(setVoiceMailNotificationTrigger(true));
    }, [dispatch]);

    // Listener for voice end call.
    const endVoiceCallListener = React.useCallback((message: any) => {
        dispatch(endOnHoldVoiceCall({ callSid: message.callSid, tenantDisconnects: message.tenantDisconnect ? message.tenantDisconnect : false }));
    }, [dispatch]);

    // Enable the add third participant button.
    const enableAddThirdParticipantButton = React.useCallback((message: any) => {
        if (message.callSid) {
            dispatch(enableAddParticipantButton(message.callSid));
        } else if (message.locationId && message.roomSid) {
            dispatch(updateVideoRoomDetailsAddParticipantButton({ locationId: message.locationId, roomSid: message.roomSid, condition: true }));
        }
    }, [dispatch]);

    // Enable the add third participant button.
    const endThirdParticipantCall = React.useCallback((message: any) => {
        if (message.callSid) {
            dispatch(terminateVoiceCall({ callerId: message.callSid, disconnectCall: false }));
        } else if (message.locationId && message.roomSid) {
            dispatch(endOnHoldVideoCall({ locationId: message.locationId, roomSid: message.roomSid, notificationId: message.notificationId, condition: true }));
            // dispatch(updateVideoRoomDetailsAddParticipantButton({ locationId: message.locationId, roomSid: message.roomSid, condition: true }));
        }
    }, [dispatch]);

    React.useEffect(() => {
        if (
            location &&
            location.pathname &&
            process.env.REACT_APP_ADMIN !== localStorage.getItem("event")
        ) {
            const inDashBoardPage: boolean = location.pathname === "/dashboard";
            setInOtherPage(!inDashBoardPage);
        }
    }, [location]);

    React.useEffect(() => {
        if (
            !deviceListenerStarted.current &&
            notificationsSliceSelector.device
        ) {
            addDeviceListeners(notificationsSliceSelector.device);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notificationsSliceSelector.device]);

    // Checks the error message toaster
    React.useEffect(() => {
        if (notificationsSliceSelector.errorMessageToaster) {
            dispatch(showToaster(
                {
                    dialogBgColor: 'bg-danger',
                    dialogMsg: notificationsSliceSelector.errorMessageToaster,
                    showDialog: true,
                    timer: '5000'
                }
            ));
            dispatch(setErrorMessageToaster(undefined));
        }
    }, [dispatch, notificationsSliceSelector.errorMessageToaster]);

    const addDeviceListeners = (device: any) => {
        deviceListenerStarted.current = true;
        device.on("incoming", handleIncomingCall);

        device.on("registered", function () {
            console.log("I'm Ready to make and receive calls!");
        });

        device.on("error", function (error: any) {
            console.log("Twilio.Device Error: " + error.message);
            logger("Twilio.Device Error:", error.message);
        });
        if (device.audio) {
            device.audio.incoming(false);
        }
    };

    // Initiate the Twilio webhook service
    const initializeDevice = React.useCallback((message_token: any) => {
        if (!device.current) {
            // eslint-disable-next-line react-hooks/exhaustive-deps
            let newDevice = new Device(message_token, { allowIncomingWhileBusy: true, closeProtection: true });
            newDevice.register();
            device.current = newDevice;
            dispatch(initializeAudioDevice(device.current));
        }
    }, []);

    let finalAudioOutputDevice: string | null = localStorage.getItem('selectedAudioOutputDevice') ? localStorage.getItem('selectedAudioOutputDevice') : "";
    React.useEffect(() => {
        if (notificationsSliceSelector.triggersetSinkId) {
            // console.log("cmng here in app.tsx");
            let audioElement: any = document.getElementById("notification_sound");
            if (authState.agentStatus === "online" && notificationsSliceSelector.notificationAudioDevice && Object.keys(notificationsSliceSelector.notificationAudioDevice)[0] !== "selectedAudioOutputDevice") {
                dispatch(setEnableDisableDropDown(""));
            }
            if (getMobileOperatingSystem() !== "Android") {
                try {
                    // console.log("inside try block");
                    if (notificationsSliceSelector.notificationAudioDevice && Object.keys(notificationsSliceSelector.notificationAudioDevice)[0] === "selectedAudioOutputDevice") {
                        // console.log("inside try and if block");
                        (audioElement) && audioElement?.setSinkId(finalAudioOutputDevice).then(() => {
                            dispatch(setEnableDisableDropDown(""));
                        });
                    }
                } catch {
                    // DisplayToaster("bg-danger", "The operating system/browser does not support selecting the audio device in App.tsx");
                    console.log("The operating system/browser does not support selecting the audio device in App.tsx");
                }
            } else {
                if (notificationsSliceSelector.notificationAudioDevice && Object.keys(notificationsSliceSelector.notificationAudioDevice)[0] === "selectedAudioOutputDevice") {
                    // console.log("in android else block");
                    dispatch(setEnableDisableDropDown(""));
                }
            }
        }
        dispatch(setAudioOutputDeviceChanged(false));

    }, [notificationsSliceSelector.triggersetSinkId]);

    React.useEffect(() => {
        if (authState.agentStatus === "busy") {
            removeBrbTimeStamp();
            removeBrbTimeout();
            stopInterval();
        }
    }, [authState.agentStatus]);

    React.useEffect(() => {
        if (socket) {
            socket.on("connect", () => { });
            setTimeout(() => {
                socket.emit("get_team_status", async (res: any) => { });
                socket.on("voiceMailNotification", voiceMailNotificationListener);
                socket.emit("get_kiosk_status", async (response: any) => { });
                logger(`while trying to get kiosk status -`, { "agentEmail": (currentUserEmail?.current ?? "not available") });
                socket.on("kiosks_status", async (res: DtoAgentStatus[]) => {
                    dispatch(setAllKioskStatusDetails(res));
                });
                socket.on("agents_status", async (res: DtoAgentStatus[]) => {
                    dispatch(setAllAgentStatusDetails(res));
                    // process only if it is agent user
                    if (process.env.REACT_APP_ADMIN !== localStorage.getItem("event")) {
                        if (currentUserEmail.current) {
                            const currentUserStatus = res.find(
                                (eachAgentStatus) =>
                                    (eachAgentStatus.email === currentUserEmail.current && eachAgentStatus.tenantID === currentUserTenantId)
                            );
                            if (currentUserStatus && currentUserStatus.status) {
                                dispatch(setAgentStatus(currentUserStatus.status));
                                if (
                                    currentUserStatusRef.current !==
                                    currentUserStatus.status
                                ) {
                                    currentUserStatusRef.current =
                                        currentUserStatus.status;
                                    if (currentUserStatus.status === "away") {
                                        if (
                                            currentUserStatus &&
                                            currentUserStatus.brbTimeout
                                        )
                                            localStorage.setItem(
                                                "brb_to",
                                                currentUserStatus.brbTimeout.toString()
                                            );
                                        if (
                                            currentUserStatus &&
                                            currentUserStatus.brbTimestamp
                                        )
                                            localStorage.setItem(
                                                "brb_ts",
                                                currentUserStatus.brbTimestamp.toString()
                                            );
                                        const currentTime = Date.now();
                                        let timeoutTime =
                                            localStorage.getItem("brb_ts");
                                        const diffInSeconds = Math.floor(
                                            (currentTime - +timeoutTime!) / 1000
                                        );
                                        if (
                                            localStorage.getItem("brb_to") &&
                                            diffInSeconds >=
                                            +localStorage.getItem("brb_to")!
                                        ) {
                                            setAppState({
                                                ...appState,
                                                showBeRightBackAlertModal: true,
                                            });
                                        } else {
                                            startBeRightBackTimer();
                                        }
                                    } else if (currentUserStatus.status === "online") {
                                        removeBrbTimeStamp();
                                        removeBrbTimeout();
                                        stopInterval();
                                    }
                                }
                            }
                        }
                    }
                });
            }, 500);
            // process only if it is agent user
            if (
                process.env.REACT_APP_ADMIN !== localStorage.getItem("event")
            ) {
                setTimeout(() => {
                    socket.emit("agent_connect", async (response: any) => {
                        initializeDevice(response.voice_call_token.token);
                    });
                    socket.on("agent_notification", notificationListener);
                    socket.on("end_voice_call", endVoiceCallListener);
                    socket.on("enable_add_third_agent", enableAddThirdParticipantButton);
                    socket.on("end_participant_call", endThirdParticipantCall);
                    socket.on("tenant_end_call", (response) => { dispatch(triggerVideoCallEndFunction({ notificationId: response.notification_id ? response.notification_id : response.location_id, isAutomateTerminate: false, IsManualCall: response.notification_id ? "no" : "yes", isTenant: "tenant" })) });
                    startDndTimer();
                    GetApiMethod("tenant/agent/my-locations")
                        .then((res) => {
                            dispatch(setLocationsData(res.data.data));
                            const allLocations: any = {};
                            res.data.data.primary.forEach((element: any) => {
                                allLocations[element["_id"]] = element;
                            });
                            res.data.data.secondary.forEach((element: any) => {
                                allLocations[element["_id"]] = element;
                            });
                            res.data.data.others.forEach((element: any) => {
                                allLocations[element["_id"]] = element;
                            });
                            dispatch(setLocationDetails({ ...allLocations }));
                        })
                        .catch((err) => {
                            console.log(err);
                        });
                }, 500);
                socket.on("connect_error", (error: any) => {
                    console.log('Socket connection error!', error);
                    infoUserForSocketIssue(error);
                    disconnectWebSocketConnection();
                });

                socket.io.on("reconnect", (res) => {
                    logger("socket got reconnected....", res);
                })

                socket.io.on("reconnect_attempt", (res) => {
                    logger("socket is trying to reconnect....", res);
                })

                socket.io.on("reconnect_error", (err) => {
                    logger("socket got reconnect_error....", err);
                })

                socket.io.on("reconnect_failed", () => {
                    logger("socket got failed to reconnect....", "");
                })

                socket.on("disconnect", (reason, details) => {
                    infoUserForSocketIssue([reason, details], false, currentUserEmail.current ? currentUserEmail.current : "no data");
                });
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [socket]);

    React.useEffect(() => {
        currentUserEmail.current = authState.users.email;
    }, [authState.users.email]);

    React.useEffect(() => {
        if (
            authState.agentStatus === "away" &&
            notificationsSliceSelector.notificationData &&
            notificationsSliceSelector.notificationData.length
        ) {
            dispatch(setAgentNotifications([...[]]));
            dispatch(playPauseRinger(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [authState.agentStatus, notificationsSliceSelector.notificationData]);

    React.useEffect(() => {
        if (notificationsSliceSelector.playNotificationRinger) {
            notificationRingerRef.current?.play();
        } else {
            notificationRingerRef.current?.pause();
        }
        let showNotificationModal: boolean = false;
        if (process.env.REACT_APP_ADMIN !== localStorage.getItem("event") && location && location.pathname && location.pathname !== '/dashboard') {
            if (notificationsSliceSelector.notificationData.length > 0) {
                showNotificationModal = true;
            }
            // if (notificationsSliceSelector.notificationData.length === 1 && ((notificationsSliceSelector.inCallNotificationIdRef === notificationsSliceSelector.notificationData[0].notificationId) || notificationsSliceSelector.holdCallDetails)) {
            //     showNotificationModal = false;
            // }
            if (notificationsSliceSelector.notificationData.length === 1 && !notificationsSliceSelector.inCallNotificationIdRef) {
                showNotificationModal = true;
            }
            if (notificationsSliceSelector.notificationData.length === 1 && ((notificationsSliceSelector.inCallNotificationIdRef === notificationsSliceSelector.notificationData[0].notificationId) || notificationsSliceSelector.holdCallDetails)) {
                showNotificationModal = false;
            }
            if (notificationsSliceSelector.notificationData.length < 0) {
                showNotificationModal = false;
            }
        }
        if (showNotificationModal) {
            setAppState({
                ...appState,
                showNotificationAlertModal: true,
            });
        } else {
            setAppState({
                ...appState,
                showNotificationAlertModal: false,
            });
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        notificationsSliceSelector.playNotificationRinger,
        location,
        notificationsSliceSelector.notificationData,
        notificationsSliceSelector.notificationData.length,
        notificationsSliceSelector.inCallNotificationIdRef,
    ]);

    const emitAgentStatus = React.useCallback(
        (status: string) => {
            if (
                socket &&
                process.env.REACT_APP_ADMIN !== localStorage.getItem("event") &&
                status
            ) {
                if (status === "away") {
                    setAppState({
                        ...appState,
                        showBeRightBackConfirmForm: true,
                    });
                } else if (status === "online") {
                    removeBrbTimeStamp();
                    removeBrbTimeout();
                    stopInterval();
                    socket.emit("set_agent_status", {
                        status: status,
                    });
                }
            }
        },
        [socket]
    );

    const startBeRightBackTimer = React.useCallback(() => {
        let timeoutTime = localStorage.getItem("brb_ts");
        if (timeoutTime) {
            if (intervalRef.current !== null) {
                window.clearInterval(intervalRef.current);
            }
            intervalRef.current = window.setInterval(() => {
                const currentTime = Date.now();
                if (localStorage.getItem("brb_ts")) {
                    const diffInSeconds = Math.floor(
                        (currentTime - +localStorage.getItem("brb_ts")!) / 1000
                    );
                    if (localStorage.getItem("brb_to") && diffInSeconds) {
                        if (diffInSeconds >= +localStorage.getItem("brb_to")!) {
                            if (intervalRef.current) {
                                window.clearInterval(intervalRef.current);
                            }
                            setAppState({
                                ...appState,
                                showBeRightBackAlertModal: true,
                            });
                        }
                    }
                }
            }, 30000);
            return () => {
                if (intervalRef.current !== null) {
                    window.clearInterval(intervalRef.current);
                }
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const stopInterval = () => {
        if (intervalRef.current) {
            window.clearInterval(intervalRef.current);
            intervalRef.current = null;
        }
    };

    const changeToOnline = React.useCallback(() => {
        setAppState({
            ...appState,
            showBeRightBackAlertModal: false,
        });
        if (
            socket &&
            process.env.REACT_APP_ADMIN !== localStorage.getItem("event")
        ) {
            socket.emit("set_agent_status", {
                status: "online",
            });
            removeBrbTimeStamp();
            removeBrbTimeout();
            stopInterval();
        }
    }, [socket]);

    const againRestartTimer = React.useCallback(() => {
        const currentTimeStamp = Date.now().toString();
        setAppState({
            ...appState,
            showBeRightBackAlertModal: false,
        });
        localStorage.setItem("brb_ts", currentTimeStamp);
        startBeRightBackTimer();
        if (
            socket &&
            process.env.REACT_APP_ADMIN !== localStorage.getItem("event")
        ) {
            socket.emit("set_agent_status", {
                status: "away",
                brbTimestamp: currentTimeStamp,
                brbTimeout: timeOutInMin * 60,
            });
        }
    }, [socket]);

    const onSaveBeRightBack = React.useCallback(() => {
        const currentTimeStamp = Date.now().toString();
        localStorage.setItem("brb_ts", currentTimeStamp);
        const brbTo = timeOutTimeMinRef.current * 60;
        localStorage.setItem("brb_to", brbTo.toString());
        setAppState({
            ...appState,
            showBeRightBackConfirmForm: false,
        });
        if (socket) {
            socket.emit("set_agent_status", {
                status: "away",
                brbTimestamp: currentTimeStamp,
                brbTimeout: timeOutTimeMinRef.current * 60,
            });
        }
    }, [setAppState, socket]);

    const onChangeBeRightBackField = React.useCallback(
        (event: any) => {
            setTimeOutInMin(event.target.value);
        },
        [setTimeOutInMin]
    );

    React.useEffect(() => {
        if (timeOutInMin) {
            timeOutTimeMinRef.current = +timeOutInMin;
        } else {
            timeOutTimeMinRef.current = 0;
        }
    }, [timeOutInMin]);

    React.useEffect(() => {
        if (
            notificationsSliceSelector.inCallNotificationIdRef &&
            notificationsSliceSelector.playNotificationRinger
        ) {
            // Removed this 2 secs ringer sound when agent is in call as per client requirement
            // setTimeout(() => {
            dispatch(playPauseRinger(false));
            notificationRingerRef.current?.pause();
            // }, 2000);
        }
        // if (
        //     notificationsSliceSelector.notificationData.length > 1 &&
        //     !notificationsSliceSelector.inCallNotificationIdRef &&
        //     !notificationsSliceSelector.playNotificationRinger
        // ) {
        //     notificationRingerRef.current?.play();
        // }
        // mute if the notification ringer if call is in hold with one notification.
        if (
            notificationsSliceSelector.notificationData.length === 1 &&
            notificationsSliceSelector.holdCallDetails &&
            notificationsSliceSelector.notificationData[0].notificationId === notificationsSliceSelector.holdCallDetails.inCallRefId &&
            !notificationsSliceSelector.inCallNotificationIdRef &&
            !notificationsSliceSelector.playNotificationRinger
        ) {
            dispatch(playPauseRinger(false));
            notificationRingerRef.current?.pause();
        }
    }, [
        notificationsSliceSelector.inCallNotificationIdRef,
        notificationsSliceSelector.playNotificationRinger,
        notificationsSliceSelector.notificationData,
    ]);

    React.useEffect(() => {
        return () => {
            if (notificationsSliceSelector.device) {
                notificationsSliceSelector.device.destroy();
            }
        };
    }, [notificationsSliceSelector.device]);

    const disconnectAudioInstanceCall = React.useCallback(() => {
        if (
            notificationsSliceSelector.disconnectCall &&
            notificationsSliceSelector.disconnectEndCallId &&
            notificationsSliceSelector.voiceCallInstance.length
        ) {
            const currentVoiceCallInstance =
                notificationsSliceSelector.voiceCallInstance.filter(
                    (voiceCallInstance: any) =>
                        checkCallSid(voiceCallInstance["customParameters"].get(
                            "call_sid"
                        )) === checkCallSid(notificationsSliceSelector.disconnectEndCallId)
                );
            if (
                currentVoiceCallInstance &&
                currentVoiceCallInstance.length > 0
            ) {
                currentVoiceCallInstance[0].disconnect();
            }
            const updateVoiceCallInstances =
                notificationsSliceSelector.voiceCallInstance.filter(
                    (voiceCallInstance: any) =>
                        checkCallSid(voiceCallInstance["customParameters"].get(
                            "call_sid"
                        )) !== checkCallSid(notificationsSliceSelector.disconnectEndCallId)
                );
            dispatch(setVoiceCallInstance([...updateVoiceCallInstances]));
            dispatch(resetDisconnectCall());
        }
    }, [
        notificationsSliceSelector.disconnectCall,
        notificationsSliceSelector.disconnectEndCallId,
        notificationsSliceSelector.voiceCallInstance,
    ]);

    React.useEffect(() => {
        disconnectAudioInstanceCall();
    }, [disconnectAudioInstanceCall]);

    React.useEffect(() => {
        return () => {
            if (authState.preventNavigation) {
                if (notificationsSliceSelector.callDetails) {
                    const callId =
                        checkCallSid(notificationsSliceSelector.callDetails[
                            "customParameters"
                        ].get("call_sid"));
                    if (callId) {
                        dispatch(
                            terminateVoiceCall({
                                callerId: callId,
                                disconnectCall: true,
                            })
                        );
                    }
                }
            }
        };
    }, [notificationsSliceSelector.callDetails]);

    React.useEffect(() => {
        if (authState.hideNavBar) {
            if (!document.body.classList.contains("kiosk-mode")) {
                document.body.classList.add("kiosk-mode");
            }
        } else {
            if (document.body.classList.contains("kiosk-mode")) {
                document.body.classList.remove("kiosk-mode");
            }
        }
    }, [authState.hideNavBar]);

    // Twilio webhook listener functionality
    const handleIncomingCall = React.useCallback(
        (call: any) => {
            try {
                logger("In handleIncomingCall function", call["customParameters"]?.get("call_sid"));
                dispatch(appendVoiceCallInstance([...[call]]));
                call.on("cancel", () => {
                    dispatch(
                        removeInComingVoiceCallNotification(
                            call["customParameters"].get("call_sid")
                        )
                    );
                    logger("In call cancel event", call["customParameters"]?.get("call_sid"));
                    // dispatch(
                    //     setRemovedCallInstance([
                    //         ...[call["customParameters"].get("call_sid")],
                    //     ])
                    // );
                    dispatch(
                        terminateVoiceCall({
                            callerId: call["customParameters"].get("call_sid"),
                            disconnectCall: false,
                        })
                    );
                    // socket.emit("end_call", {});
                });
                call.on("disconnect", function (message: any) {
                    dispatch(
                        terminateVoiceCall({
                            callerId: message["customParameters"].get("call_sid"),
                            disconnectCall: true,
                        })
                    );
                    logger("In call disconnect event", call["customParameters"].get("call_sid"));
                });
            } catch (error) {
                console.log("error in handleIncomingCall function", error);
            }

        },
        [dispatch, logger]
    );

    // Audio page call timer functionality
    React.useEffect(() => {
        let interval: any = 0;
        if (notificationsSliceSelector.audioCallStartTimer) {
            interval = setInterval(() => {
                dispatch(setAudioCallTimer(1000));
            }, 1000)
        } else {
            clearInterval(interval);
        }
        return () => clearInterval(interval)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [notificationsSliceSelector.audioCallStartTimer])

    // DoNotDisturb Timer functionality
    const startDndTimer = React.useCallback(() => {
        let interval: any = 0;
        interval = setInterval(() => {
            dispatch(updateDndTimer());
        }, 1000)

        return () => clearInterval(interval)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    React.useEffect(() => {
        if (!localStorage.getItem("access_Token") && !authState.isAuthenticated) {
            setAppState({
                ...appState,
                showNotificationAlertModal: false,
                showBeRightBackAlertModal: false,
                showBeRightBackConfirmForm: false,
            });
            setInOtherPage(false);
            dispatch(resetAgentNotifications());
        }
    }, [authState.isAuthenticated]);

    React.useEffect(() => {
        if (isLogin()) {
            if (!fetchingUserData.current) {
                fetchingUserData.current = true;
                fetchUserDetails();
            }
        } else {
            dispatch(fetchingUserDetails(false));
            if (location.pathname !== "/tenantRoom") {
                localStorage.clear();
            }
            dispatch(clearAuthSlice());
        }
        const checkAccessTokenExist = () => {
            if (!localStorage.getItem("access_Token") && location.pathname !== "/tenantRoom") {
                localStorage.clear();
                dispatch(clearAuthSlice());
                if (location.pathname !== "/kiosk-video") {
                    navigate("/sign-in");
                }
                disconnectWebSocketConnection();
            }
        };
        window.addEventListener("storage", checkAccessTokenExist);
        return () => {
            window.removeEventListener("storage", checkAccessTokenExist);
        };
    }, []);

    return (
        <>
            {authenticated && !authState.hideNavBar && (
                <NavigationBar emitAgentStatus={emitAgentStatus} />
            )}
            {authState.loading ? <AppLoader /> : <AppRoutes />}
            <ToastView />
            {process.env.REACT_APP_ADMIN !== localStorage.getItem("event") && (
                <audio
                    id="notification_sound"
                    className="audio-element"
                    ref={notificationRingerRef}
                    src={settingsState?.ringTone ? settingsState.ringTone : ringer}
                    loop={notificationsSliceSelector.loopRinger}
                />
            )}
            {appState.showNotificationAlertModal && (
                <AppModal
                    title={"Notification"}
                    bodyText={
                        "You have received notification(s). In order to see the notification, you need to be on the Dashboard page."
                    }
                    primaryButtonLabel={"Redirect to Dashboard page"}
                    secondaryButtonLabel={"Stay in current page"}
                    primaryButtonEventEmitter={redirectToDashboardPage}
                    secondaryButtonEventEmitter={() => {
                        // setInOtherPage(false);
                        setAppState({
                            ...appState,
                            showNotificationAlertModal: false,
                        });
                    }}
                />
            )}
            {appState.showBeRightBackAlertModal && (
                <AppModal
                    title={"Status"}
                    bodyText={
                        "You set your status as 'Be Right Back' few  minutes back, do you want to change your status to 'Online'?"
                    }
                    primaryButtonLabel={"Yes"}
                    secondaryButtonLabel={"No"}
                    primaryButtonEventEmitter={changeToOnline}
                    secondaryButtonEventEmitter={againRestartTimer}
                />
            )}
            {appState.showBeRightBackConfirmForm && (
                <AppModal
                    title={"Status"}
                    bodyText={
                        "How long you would be in `Be Right Back` status?"
                    }
                    primaryButtonLabel={"Save"}
                    primaryButtonEventEmitter={() => onSaveBeRightBack()}
                    secondaryButtonLabel={"Cancel"}
                    secondaryButtonEventEmitter={() => {
                        setAppState({
                            ...appState,
                            showBeRightBackConfirmForm: false,
                        });
                    }}
                    disablePrimaryButton={
                        timeOutInMin > 120 || timeOutInMin <= 0 ? true : false
                    }
                >
                    <Form className="mt-2">
                        <Form.Group
                            className="mb-3"
                            controlId="exampleForm.ControlInput1"
                        >
                            <div className="row">
                                <div className="col">
                                    <Form.Control
                                        type="text"
                                        value={timeOutInMin}
                                        onChange={onChangeBeRightBackField}
                                        onKeyPress={numberOnly}
                                    />
                                </div>
                                <div className="col mt-1">
                                    <Form.Label>minutes</Form.Label>
                                </div>
                            </div>
                            {timeOutInMin > 120 && (
                                <p className="text-danger mt-2">
                                    Should not be more than 120 minutes.
                                </p>
                            )}
                            {timeOutInMin <= 0 && (
                                <p className="text-danger mt-2">
                                    Enter value between 1 minute to 120 minutes.
                                </p>
                            )}
                        </Form.Group>
                    </Form>
                </AppModal>
            )}
        </>
    );
}

export default App;
